import { FormBuilder } from "@k3-tech/react-kit";
import { useState } from "react";
import { z } from "zod";
import { usePricelistSalesChannelDelete } from "../usecases/usePricelistSalesChannelDelete";
import { usePricelistSalesChannelUpsert } from "../usecases/usePricelistSalesChannelUpsert";

type PricelistSalesChannelUpsertFormData = {
	/** Display only */
	channelName: string;
	priceMarkupPerc?: number | string;
};

// A cleared number input reaches the form as "", null, undefined or NaN depending on the browser.
const isBlankMarkup = (value: unknown) => {
	if (value === null || value === undefined) return true;
	if (typeof value === "number") return Number.isNaN(value);
	return String(value).trim() === "";
};

export default function PricelistSalesChannelUpsertDialog({
	pricelistID,
	salesChannelID,
	channelName,
	priceMarkupPerc,
	pricelistSalesChannelID,
	onSuccess,
	onCancel,
}: {
	pricelistID: string;
	salesChannelID: string;
	channelName: string;
	/** Undefined when this pricelist has no markup of its own yet. */
	priceMarkupPerc?: number;
	pricelistSalesChannelID?: string;
	onSuccess: (message: string) => void;
	onCancel: () => void;
}) {
	const { upsertPricelistSalesChannel } = usePricelistSalesChannelUpsert();
	const { deletePricelistSalesChannel } = usePricelistSalesChannelDelete();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const saveMarkup = async (data: PricelistSalesChannelUpsertFormData) => {
		const isBlank = isBlankMarkup(data.priceMarkupPerc);
		const raw = isBlank ? "" : String(data.priceMarkupPerc).trim();

		// Empty or 0 drops the pricelist's own markup, so the channel default applies again.
		if (isBlank || Number(raw) === 0) {
			if (!pricelistSalesChannelID) {
				onCancel();
				return;
			}
			const result = await deletePricelistSalesChannel(pricelistSalesChannelID);
			if (!result.error) {
				onSuccess(`${channelName} follows the channel default again`);
			}
			return;
		}

		const result = await upsertPricelistSalesChannel({
			pricelistID,
			salesChannelID,
			priceMarkupPerc: Number(raw),
			isActive: true,
		});
		if (!result.error) {
			onSuccess(`${channelName} markup saved (${Number(raw)}%)`);
		}
	};

	// FormBuilder only shows its submitting state when the caller passes it; the save recomputes every channel price, so the wait is visible.
	const onSubmit = async (data: PricelistSalesChannelUpsertFormData) => {
		setIsSubmitting(true);
		try {
			await saveMarkup(data);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<FormBuilder<PricelistSalesChannelUpsertFormData>
			defaultValues={{ channelName, priceMarkupPerc }}
			onSubmit={onSubmit}
			onCancel={onCancel}
			isSubmitting={isSubmitting}
			actionsClassName="justify-end"
			showActionsSeparator={false}
			sections={[
				{
					layout: "grid",
					grid: { cols: 1, gap: "gap-4" },
					fields: [
						{
							name: "channelName",
							label: "Channel",
							type: "text",
							disabled: true,
						},
						{
							name: "priceMarkupPerc",
							label: "Markup (%)",
							type: "number",
							required: false,
							description:
								"Leave empty or enter 0 to follow the channel default instead of this pricelist's own markup.",
							// FormBuilder's generated schema rejects every shape an emptied number
							// input takes, which is the one value that has to get through here.
							validation: z
								.any()
								.refine(
									(value) =>
										isBlankMarkup(value) ||
										(Number(value) >= 0 && Number(value) < 100),
									"Markup must be between 0 and 99.99",
								),
						},
					],
				},
			]}
			submitLabel="Save"
			cancelLabel="Cancel"
		/>
	);
}
